import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { Loader } from '@googlemaps/js-api-loader'
import { useRouter } from 'next/navigation'

import ErrorService from '@services/error'

type ContextValue = {
  mapsInitialized: boolean
  initMaps: () => void
}

const defaultValue: ContextValue = {
  mapsInitialized: false,
  initMaps: () => undefined,
}

const GoogleMapsContext = createContext<ContextValue>(defaultValue)

interface Props {
  children: ReactNode
  apiKey?: string
}

export function GoogleMapsProvider(props: Props) {
  const { children, apiKey } = props
  const [maps, setMaps] = useState<typeof google.maps | null>(null)
  const [loadRequested, setLoadRequested] = useState(false)
  const router = useRouter()

  const initMaps = useCallback(() => {
    setLoadRequested(true)
  }, [])

  useEffect(() => {
    if (!apiKey || maps || !loadRequested) return

    const init = async () => {
      const googleInstance = await new Loader({
        apiKey,
        libraries: ['places', 'geometry'],
      }).load()

      setMaps(googleInstance.maps)
    }

    init().catch((error) => {
      ErrorService.handleError({ error, router })
    })
  }, [apiKey, loadRequested, maps, router])

  const value = useMemo<ContextValue>(() => {
    return {
      mapsInitialized: !!maps,
      initMaps,
    }
  }, [initMaps, maps])

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  )
}

type UseGoogleMapsOptions = {
  autoInit?: boolean
}

export function useGoogleMaps(options?: UseGoogleMapsOptions) {
  const context = useContext(GoogleMapsContext)
  const autoInit = options?.autoInit ?? true
  const { initMaps } = context

  useEffect(() => {
    if (autoInit) {
      initMaps()
    }
  }, [autoInit, initMaps])

  return context
}
